import {
  useRltOrderShowQuery,
  useRltOrderUpdatePostalAddressMutation,
  useRltOrderUpdateRepresentativeMutation,
  useRltOrderUpdateServiceAddressMutation,
} from "@/services/backend/rlt/orders/api/order-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsHeader,
  DocumentSheetContent,
  DocumentSheetContextProvider,
  DocumentSheetHeader,
  DocumentSheetMain,
  DocumentSheetTitle,
  DocumentSheetTrigger,
  useDocumentSheetContext,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import t from "@/lang/lang";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { TreatmentComponent } from "@/services/backend/rlt/treatments/components/treatment-component";
import { Column } from "@/services/backend/rlt/treatments/lib/treatment-component-state";
import { Button } from "@/shared/components/ui/button";
import { Order } from "@/services/backend/rlt/orders/types/order";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { NavigateToDocumentButton } from "@/services/backend/documents/components/preview/_components/navigate-to-document-button";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { RefreshCw } from "lucide-react";
import { OrderCard } from "@/routes/processes/[processId]/rlt/orders/[orderId]/data/_components/order-card";
import {
  DocumentPage,
  DocumentPageContent,
  DocumentPageHeader,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { PostalAddressCardWithMutation } from "@/services/backend/addresses/components/postal-address-card";
import { ServiceAddressCardWithMutation } from "@/services/backend/addresses/components/service-address-card";
import { RepresentativeCardWithMutation } from "@/services/backend/representatives/components/representative-card";

export function RltOrderPreview({ orderId }: { orderId: string }) {
  const {
    data: order,
    isLoading,
    error,
  } = useRltOrderShowQuery({ id: orderId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return <RefreshCw className="h-4 w-4" />;
  }

  return (
    <DocumentSheetContextProvider>
      <OrderDocumentSheet order={order!} />
    </DocumentSheetContextProvider>
  );
}

function OrderDocumentSheet({ order }: { order: Order }) {
  const { open, setOpen } = useDocumentSheetContext();

  return (
    <DocumentSheet open={open} onOpenChange={setOpen}>
      <DocumentSheetTrigger asChild>
        <Button variant="ghost" size="sm">
          {t("Vorschau")}
        </Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>
            {t(order!.documentKind)} {t("Vorschau")}
          </DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <OrderPreview order={order!} />
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function OrderPreview({ order }: { order: Order }) {
  return (
    <>
      <OrderView order={order} />
      <OrderActions order={order} />
    </>
  );
}

function OrderView({ order }: { order: Order }) {
  return (
    <div className="md:col-span-4">
      <PersistenceContextProvider>
        <DocumentPage>
          <Tabs defaultValue="order">
            <DocumentPageHeader>
              <TabsList>
                <TabsTrigger value="order">{t(order.documentKind)}</TabsTrigger>
                <TabsTrigger value="treatment">{t("Leistungen")}</TabsTrigger>
                <TabsTrigger value="customer-address">
                  {t("Anschrift")}
                </TabsTrigger>
                <TabsTrigger value="representative">
                  {t("Betreuer")}
                </TabsTrigger>
              </TabsList>
              <PersistenceStateUsingContext className="mr-2 ml-auto" />
            </DocumentPageHeader>
            <DocumentPageContent className="max-h-[75vh] overflow-auto">
              <TabsContent value="order">
                <OrderCard order={order} />
              </TabsContent>
              <TabsContent value="treatment">
                <TreatmentComponent
                  treatmentId={order.treatmentId}
                  availableColumns={[
                    Column.Position,
                    Column.ComponentKind,
                    Column.ComponentData,
                    Column.Cycle,
                    Column.Formula,
                    Column.CleanTime,
                    Column.BasePrice,
                    Column.CycleDiscount,
                    Column.SpecialDiscount,
                    Column.FinalPrice,
                    Column.Comment,
                    Column.Actions,
                  ]}
                  defaultColumns={[
                    Column.Position,
                    Column.ComponentKind,
                    Column.BasePrice,
                    Column.CycleDiscount,
                    Column.FinalPrice,
                    Column.Comment,
                    Column.Actions,
                  ]}
                />
              </TabsContent>
              <TabsContent value="customer-address">
                <div className="space-y-2">
                  <PostalAddressCardWithMutation
                    id={order!.id}
                    address={order!.postalAddress}
                    mutation={useRltOrderUpdatePostalAddressMutation}
                  />
                  <ServiceAddressCardWithMutation
                    id={order!.id}
                    address={order!.serviceAddress}
                    mutation={useRltOrderUpdateServiceAddressMutation}
                  />
                </div>
              </TabsContent>
              <TabsContent value="representative">
                <RepresentativeCardWithMutation
                  id={order!.id}
                  representative={order!.representative}
                  mutation={useRltOrderUpdateRepresentativeMutation}
                />
              </TabsContent>
            </DocumentPageContent>
          </Tabs>
        </DocumentPage>
      </PersistenceContextProvider>
    </div>
  );
}

function OrderActions({ order }: { order: Order }) {
  return (
    <DocumentSheetActions className="md:col-span-1">
      <DocumentSheetActionsHeader
        title={t(order.documentKind)}
        documentNumber={<DocumentNumberById documentId={order.id} />}
      />
      <DocumentSheetActionsContent>
        <NavigateToDocumentButton
          documentId={order.id}
          documentKind={order.documentKind}
          processId={order.processId}
          serviceKind={GSMServiceKind.RLT}
        />
      </DocumentSheetActionsContent>
    </DocumentSheetActions>
  );
}
